import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { isStale } from '../api/problem';
import { nameRefusal, type NameSubmitResult } from '../ui/NamePanel';
import type { TenantRow, usePlatformHeaders } from './use-tenants';

/** Create and rename: each answers the name panel with its result, and says so in a toast. */
export function useTenantNames(headers: ReturnType<typeof usePlatformHeaders>, reload: () => void) {
  const { t } = useTranslation('administration');
  const create = async (name: string): Promise<NameSubmitResult> => {
    if (headers === null) {
      return { ok: false, form: t('problems.ACTIVE_CONTEXT_REQUIRED') };
    }
    const { data, error, response } = await apiClient.POST('/v1/administration/platform/tenants', {
      params: { header: headers },
      body: { name },
    });
    if (data === undefined || !response.ok) {
      return nameRefusal(t, error, response.status, 'TENANT_NAME_TAKEN');
    }
    toast.success(t('tenants.toast.created', { name: data.name }));
    reload();
    return { ok: true };
  };

  const rename = async (row: TenantRow, name: string): Promise<NameSubmitResult> => {
    if (headers === null) {
      return { ok: false, form: t('problems.ACTIVE_CONTEXT_REQUIRED') };
    }
    const { data, error, response } = await apiClient.PATCH(
      '/v1/administration/platform/tenants/{tenantId}',
      { params: { path: { tenantId: row.id }, header: headers }, body: { name } },
    );
    if (data === undefined || !response.ok) {
      if (isStale(error)) {
        reload();
      }
      return nameRefusal(t, error, response.status, 'TENANT_NAME_TAKEN');
    }
    toast.success(t('tenants.toast.renamed', { name: data.name }));
    reload();
    return { ok: true };
  };
  return { create, rename };
}
